<template>
  <v-card
    v-resize="onRenderChart"
    :color="bgColor"
    :dark="dark"
    :loading="loading"
    style="position: relative;"
    dense
  >
    <v-overlay
      :value="loading"
      absolute
      color="white"
    >
      <v-progress-circular
        :size="50"
        :color="dark ? void 0 : 'primary'"
        indeterminate
      />
    </v-overlay>

    <v-card-text class="b-flex flex-column" style="min-height: 100%;">
      <div
        class="title font-weight-light mb-2"
        v-text="title"
      />

      <div
        :style="{ height }"
        style="flex-grow: 1; margin: 0 -16px -16px;"
      >
        <canvas
          v-show="!loading"
          ref="canvas"
          :height="height"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import debounce from 'lodash/debounce'


const DEBOUNCE_TIME = 100

const getAxes = (dark = false) => ({
  gridLines: {
    color: dark ? 'rgba(255,255,255,0.5)' : void 0,
    borderDash: [4, 2],
  },
  ticks: {
    fontSize: 10,
    fontColor: dark ? 'rgba(255,255,255,0.5)' : void 0,
  },
})

const getLineChartOptions = (dark = false) => {
  const axes = getAxes(dark)

  return {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    scales: {
      xAxes: [axes],
      yAxes: [axes],
    },
  }
}

const getChart = () => import(
  /* webpackChunkName: "chart" */
  'chart.js'
)

export default {
  name: 'DashboardChartLine',
  props: {
    color: String,
    bgColor: String,
    dark: Boolean,
    title: String,
    loading: Boolean,
    height: [String, Number],
    labels: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
  },
  computed: {
    dataset() {
      return {
        fill: false,
        borderColor: this.color,
        data: this.data,
      }
    },
    lineChartData() {
      return {
        labels: this.labels,
        datasets: [this.dataset],
      }
    },
  },
  watch: {
    data: 'onRenderChart',
  },
  created() {
    this.onRenderChart = debounce(
      this.onRenderChart,
      DEBOUNCE_TIME,
    )
  },
  mounted() {
    this.onRenderChart()
  },
  beforeDestroy() {
    const { chart } = this
    if (chart) chart.destroy()
  },
  methods: {
    async onRenderChart() {
      const options = getLineChartOptions(this.dark)
      const { chart } = this

      if (chart) {
        chart.data = this.lineChartData
        chart.options = options
        chart.update({ duration: 0, lazy: false, easing: 'linear' })
        return
      }

      if (!this.$refs.canvas) return
      const ctx = this.$refs.canvas.getContext('2d')

      const Chart = await getChart()

      this.chart = Chart.Line(ctx, {
        data: this.lineChartData,
        options,
      })
    },
  },
}
</script>
